import React from 'react';
import { Box, Flex, Icon, Skeleton, Text } from '@chakra-ui/react';
import { IconProps } from 'phosphor-react';
import { UI, RADII } from '../ui';
import { InfoPopover } from './InfoPopover';

interface KpiCardProps {
  label: string;
  value: string | number;
  icon: React.ComponentType<IconProps>;
  iconColor: string;
  iconBg: string;
  sub?: string;
  isLoading?: boolean;
  onClick?: () => void;
  /** Conteúdo do infotip (o que é / dados cruzados / filtro). */
  info?: React.ReactNode;
  /** Título do infotip (default: o próprio label). */
  infoTitle?: string;
  /** Conteúdo extra abaixo do subtítulo (ex.: badge de ressalva). */
  footer?: React.ReactNode;
}

/** Card de KPI clicável (label, valor grande, ícone colorido, subtítulo opcional). */
export function KpiCard({
  label,
  value,
  icon,
  iconColor,
  iconBg,
  sub,
  isLoading,
  onClick,
  info,
  infoTitle,
  footer,
}: KpiCardProps) {
  const valStr = String(value);
  // a fonte do valor diminui conforme ele cresce — cabe em 1 linha sem truncar nem
  // quebrar (números da carteira chegam a "R$ 94.687.480,61").
  const valueFont = ((): string => {
    const n = valStr.length;
    if (n > 15) return '16px';
    if (n > 12) return '18px';
    if (n > 9) return '21px';
    if (n > 6) return '24px';
    return '27px';
  })();
  return (
    <Box
      onClick={onClick}
      bg={UI.glass}
      border="1px solid"
      borderColor={UI.glassBorder}
      borderRadius={RADII.card}
      p="18px 20px"
      backdropFilter={UI.glassBlur}
      boxShadow={UI.glassShadow}
      cursor={onClick ? 'pointer' : 'default'}
      transition="all .14s ease"
      _hover={
        onClick
          ? {
              transform: 'translateY(-2px)',
              borderColor: UI.brand,
              boxShadow: '0 6px 18px rgba(15,23,42,.10)',
            }
          : undefined
      }
    >
      <Flex align="flex-start" justify="space-between" mb={3}>
        <Flex align="center" gap={1}>
          <Text
            fontSize="10.5px"
            fontWeight={700}
            letterSpacing=".9px"
            color={UI.muted}
            textTransform="uppercase"
          >
            {label}
          </Text>
          {info && <InfoPopover title={infoTitle ?? label}>{info}</InfoPopover>}
        </Flex>
        <Flex
          align="center"
          justify="center"
          w="30px"
          h="30px"
          borderRadius="9px"
          flexShrink={0}
          style={{ background: iconBg, color: iconColor }}
        >
          <Icon as={icon} boxSize="17px" weight="bold" />
        </Flex>
      </Flex>
      {isLoading ? (
        <Skeleton height="26px" width="80%" />
      ) : (
        <Text
          fontSize={valueFont}
          fontWeight={700}
          letterSpacing="-0.5px"
          color={UI.ink}
          lineHeight="1.1"
          title={valStr}
        >
          {value}
        </Text>
      )}
      {sub && (
        <Text fontSize="12px" fontWeight={600} mt={2} color={UI.muted}>
          {sub}
        </Text>
      )}
      {footer}
    </Box>
  );
}
